import type { Donation } from "./types";

const MAX_UNWRAP_DEPTH = 4;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function looksLikeAlert(record: Record<string, unknown>): boolean {
  return (
    record.id != null &&
    (record.amount != null ||
      record.amount_main != null ||
      record.amount_in_user_currency != null)
  );
}

/**
 * Socket.IO sends the alert object (or its JSON string).
 * Centrifugo publications nest it as `{ data: { seq, data: alert } }`.
 */
function unwrapAlert(value: unknown, depth = 0): Record<string, unknown> | null {
  if (depth > MAX_UNWRAP_DEPTH || !isRecord(value)) {
    return null;
  }

  if (looksLikeAlert(value)) {
    return value;
  }

  if ("data" in value) {
    const nested = unwrapAlert(value.data, depth + 1);
    if (nested) {
      return nested;
    }
  }

  if ("result" in value) {
    return unwrapAlert(value.result, depth + 1);
  }

  return null;
}

/**
 * Normalize a DonationAlerts donation from the legacy Socket.IO shape
 * or the current alert payload (`amount_in_user_currency`, `payin_system`).
 * Returns null when the message is not a type-1 donation that should be shown.
 */
export function parseDonationPayload(raw: string | Record<string, unknown>): Donation | null {
  let parsed: unknown = raw;

  if (typeof raw === "string") {
    if (!raw) {
      return null;
    }
    parsed = JSON.parse(raw);
  }

  const alert = unwrapAlert(parsed);
  if (!alert) {
    return null;
  }

  if (alert.is_shown != null && Number(alert.is_shown) === 1) {
    return null;
  }

  const alertType =
    alert.alert_type == null || alert.alert_type === "" ? "1" : String(alert.alert_type);
  if (alertType !== "1") {
    return null;
  }

  const amount = Number(alert.amount);
  const amountMain = Number(alert.amount_main ?? alert.amount_in_user_currency);
  const currency = alert.currency;

  if (
    alert.id == null ||
    alert.id === "" ||
    typeof currency !== "string" ||
    !currency ||
    !Number.isFinite(amount) ||
    amount <= 0 ||
    !Number.isFinite(amountMain)
  ) {
    return null;
  }

  const billing = alert.billing_system ?? alert.payin_system;

  return {
    id: String(alert.id),
    alertType,
    amount,
    currency,
    amountMain,
    billingSystem: billing == null ? "" : String(billing),
  };
}
